import { useEffect, useMemo, useState } from 'react'
import { Button, IconButton } from '../ui-kitNew/buttonNew'
import { Card, CardHeader, EmptyState } from '../ui-kitNew/cardNew'
import { Segmented } from '../ui-kitNew/controlsNew'
import { Popover } from '../ui-kitNew/popoverNew'
import { Select } from '../ui-kitNew/selectNew'
import { MultiSelectList } from '../ui-kitNew/multi-selectNew'
import { cn } from '../ui-kitNew/cnNew'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ColumnsIcon,
  DownloadIcon,
  FilterIcon,
  RotateIcon,
  TableIcon,
} from '../ui-kitNew/iconsNew'
import { FIELDS, getField, groupFields } from "../../lib/reportNew/fieldsNew"
import { applyFilterGroups, countActiveConditions } from "../../lib/reportNew/filtersNew"
import { formatFieldValue, formatNumber } from "../../lib/reportNew/formatNew"
import { ColumnFilterMenu } from '../filtersNew/column-filter-menuNew'

const PAGE_SIZES = [10, 25, 50, 100]

function compare(a, b, field) {
  const va = a[field.key]
  const vb = b[field.key]
  if (va === vb) return 0
  if (va === null || va === undefined || va === '') return 1
  if (vb === null || vb === undefined || vb === '') return -1
  if (field.type === 'number') return Number(va) - Number(vb)
  if (field.type === 'boolean') return Number(va) - Number(vb)
  if (field.type === 'date') return String(va).localeCompare(String(vb))
  return String(va).localeCompare(String(vb), 'es', { numeric: true })
}

function toCsv(rows, fields) {
  const header = fields.map((field) => `"${field.label}"`).join(';')
  const body = rows
    .map((row) =>
      fields
        .map((field) => {
          const raw = row[field.key]
          if (raw === null || raw === undefined) return ''
          if (field.type === 'number') return String(raw).replace('.', ',')
          return `"${String(raw).replace(/"/g, '""')}"`
        })
        .join(';')
    )
    .join('\n')
  return `${header}\n${body}`
}

/**
 * Tabla de datos totalmente dirigida por metadatos:
 * columnas, ordenación, filtros por columna, visibilidad, densidad,
 * totales de columnas numéricas y exportación se generan a partir de FIELDS.
 */
export function DataTable({
  rows = [],
  fields = FIELDS,
  title = 'Detalle de registros',
  subtitle,
  defaultVisible,
  onRowClick,
}) {
  const [columnFilters, setColumnFilters] = useState({})
  const [sort, setSort] = useState({ key: '', dir: '' })
  const [density, setDensity] = useState('cozy')
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(0)
  const [visible, setVisible] = useState(
    () => defaultVisible || fields.slice(0, 9).map((field) => field.key)
  )

  const visibleFields = fields.filter((field) => visible.includes(field.key))
  const groups = Object.entries(columnFilters)

  const filtered = useMemo(
    () => applyFilterGroups(rows, groups.map(([, group]) => group)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, columnFilters]
  )

  /** Datos para las facetas de cada columna: todos los filtros menos el propio. */
  const facetRowsFor = (key) =>
    applyFilterGroups(
      rows,
      groups.filter(([columnKey]) => columnKey !== key).map(([, group]) => group)
    )

  const sorted = useMemo(() => {
    if (!sort.key || !sort.dir) return filtered
    const field = getField(sort.key)
    if (!field) return filtered
    const copy = [...filtered]
    copy.sort((a, b) => (sort.dir === 'asc' ? compare(a, b, field) : compare(b, a, field)))
    return copy
  }, [filtered, sort])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  useEffect(() => {
    if (page > totalPages - 1) setPage(0)
  }, [page, totalPages])

  const pageRows = sorted.slice(page * pageSize, page * pageSize + pageSize)
  const activeColumnFilters = groups.reduce((acc, [, group]) => acc + countActiveConditions(group), 0)

  const totals = useMemo(() => {
    const result = {}
    visibleFields
      .filter((field) => field.type === 'number')
      .forEach((field) => {
        result[field.key] = sorted.reduce((acc, row) => acc + (Number(row[field.key]) || 0), 0)
      })
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, visible])

  const exportCsv = () => {
    const blob = new Blob([`\uFEFF${toCsv(sorted, visibleFields)}`], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'registros-filtrados.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const columnOptions = groupFields(fields).flatMap((group) =>
    group.fields.map((field) => ({
      value: field.key,
      label: field.label,
      hint: group.name,
    }))
  )

  const rowPadding = { compact: 'py-1.5', cozy: 'py-2.5', comfortable: 'py-3.5' }[density]

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={title}
        subtitle={
          subtitle ||
          `${sorted.length.toLocaleString('es-ES')} registros · ${visibleFields.length} columnas visibles`
        }
        icon={
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <TableIcon size={16} />
          </span>
        }
        actions={
          <div className="flex items-center gap-1.5">
            {activeColumnFilters > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setColumnFilters({})}>
                <RotateIcon size={14} />
                {`Quitar ${activeColumnFilters} filtro${activeColumnFilters > 1 ? 's' : ''}`}
              </Button>
            )}

            <Segmented
              ariaLabel="Densidad de la tabla"
              value={density}
              onChange={setDensity}
              options={[
                { value: 'compact', label: 'S', title: 'Densidad compacta' },
                { value: 'cozy', label: 'M', title: 'Densidad normal' },
                { value: 'comfortable', label: 'L', title: 'Densidad amplia' },
              ]}
            />

            <Popover
              align="end"
              width={280}
              renderTrigger={({ toggle, props }) => (
                <Button variant="outline" size="sm" onClick={toggle} {...props}>
                  <ColumnsIcon size={15} />
                  Columnas
                </Button>
              )}
            >
              <div className="flex flex-col gap-2">
                <p className="text-[13px] font-semibold text-foreground">Columnas visibles</p>
                <MultiSelectList
                  options={columnOptions}
                  value={visible}
                  onChange={(next) => setVisible(next.length ? next : [fields[0].key])}
                  searchPlaceholder="Buscar columna…"
                />
              </div>
            </Popover>

            <Button variant="outline" size="sm" onClick={exportCsv}>
              <DownloadIcon size={15} />
              CSV
            </Button>
          </div>
        }
      />

      <div className="scrollbar-slim overflow-x-auto border-t border-border">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-muted/70">
              {visibleFields.map((field) => {
                const group = columnFilters[field.key]
                const isSorted = sort.key === field.key && sort.dir
                return (
                  <th
                    key={field.key}
                    scope="col"
                    aria-sort={
                      isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    style={{ minWidth: field.table?.width || 140 }}
                    className="group/th border-b border-border px-3 py-2 whitespace-nowrap"
                  >
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setSort((prev) =>
                            prev.key === field.key
                              ? {
                                  key: field.key,
                                  dir: prev.dir === 'asc' ? 'desc' : prev.dir === 'desc' ? '' : 'asc',
                                }
                              : { key: field.key, dir: 'asc' }
                          )
                        }
                        className={cn(
                          'flex min-w-0 items-center gap-1 text-[12px] font-semibold tracking-wide uppercase transition-colors',
                          isSorted ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <span className="truncate">{field.label}</span>
                        {field.unit && (
                          <span className="font-normal normal-case opacity-70">{`(${field.unit})`}</span>
                        )}
                        {isSorted &&
                          (sort.dir === 'asc' ? (
                            <ArrowUpIcon size={13} />
                          ) : (
                            <ArrowDownIcon size={13} />
                          ))}
                      </button>

                      <ColumnFilterMenu
                        fieldKey={field.key}
                        rows={rows}
                        facetRows={facetRowsFor(field.key)}
                        group={group}
                        onChange={(next) =>
                          setColumnFilters((prev) => {
                            const copy = { ...prev }
                            if (!next.conditions.length) delete copy[field.key]
                            else copy[field.key] = next
                            return copy
                          })
                        }
                        sort={sort.key === field.key ? sort.dir : ''}
                        onSort={(dir) => setSort({ key: field.key, dir })}
                        onHide={() => setVisible((prev) => prev.filter((key) => key !== field.key))}
                        canHide={visibleFields.length > 1}
                      />
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {pageRows.map((row, index) => (
              <tr
                key={row.id || index}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-border/70 transition-colors hover:bg-accent/60',
                  onRowClick && 'cursor-pointer'
                )}
              >
                {visibleFields.map((field) => (
                  <td
                    key={field.key}
                    className={cn(
                      'px-3 text-[13px] text-foreground',
                      rowPadding,
                      field.type === 'number' && 'text-right tabular-nums',
                      field.table?.mono && 'font-mono text-[12.5px]'
                    )}
                  >
                    {formatFieldValue(field.key, row[field.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          {sorted.length > 0 && (
            <tfoot>
              <tr className="bg-muted/70">
                {visibleFields.map((field, index) => (
                  <td
                    key={field.key}
                    className={cn(
                      'border-t border-border px-3 py-2 text-[12.5px] font-semibold whitespace-nowrap',
                      field.type === 'number' ? 'text-right tabular-nums text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {field.type === 'number'
                      ? `${formatNumber(totals[field.key], field.decimals ?? 0)}${field.unit ? ` ${field.unit}` : ''}`
                      : index === 0
                        ? 'Totales'
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {sorted.length === 0 && (
        <div className="p-4">
          <EmptyState
            icon={<FilterIcon size={22} />}
            title="Ningún registro coincide con los filtros"
            description="Prueba a relajar alguna condición o a quitar los filtros de columna."
            action={
              <Button variant="secondary" size="sm" onClick={() => setColumnFilters({})}>
                Quitar filtros de columna
              </Button>
            }
          />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">Filas por página</span>
          <Select
            size="sm"
            className="w-20"
            options={PAGE_SIZES.map((value) => ({ value, label: String(value) }))}
            value={pageSize}
            onChange={(value) => {
              setPageSize(Number(value))
              setPage(0)
            }}
            ariaLabel="Filas por página"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">
            {sorted.length
              ? `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, sorted.length)} de ${sorted.length.toLocaleString('es-ES')}`
              : '0 resultados'}
          </span>
          <IconButton
            label="Página anterior"
            disabled={page === 0}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          >
            <ChevronLeftIcon size={16} />
          </IconButton>
          <span className="text-[12.5px] font-medium text-foreground">{`${page + 1} / ${totalPages}`}</span>
          <IconButton
            label="Página siguiente"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
          >
            <ChevronRightIcon size={16} />
          </IconButton>
        </div>
      </div>
    </Card>
  )
}
